import { type Card } from '@cribbage/engine';
import { useTranslation } from 'react-i18next';
import { cardName } from '~/i18n/cardName.js';
import { type CardBack } from '~/store/cardBacks.js';
import { useDrawnCardBack, useDrawnCardFront } from '~/store/membership.js';
import { cardFrontOrDefault, type CardFront } from '~/store/cardFronts.js';
import { CardFaceArt } from './CardFaceArt.js';
import { CardSvg } from './CardSvg.js';

/**
 * The one card on the table.
 *
 * A card is either a control the player operates — a card in their own hand,
 * which is a real `<button>` with a real name — or scenery, which is inert and
 * hidden from assistive tech. There is no third kind, and nothing in between:
 * the e2e suite and every screen reader find the hand by looking for
 * `button[aria-pressed]` named "seven of clubs", and every other card on the
 * table must stay out of that result.
 *
 * Flipping cards carry both faces so the motion layer can toggle a class
 * without changing the accessible tree. Both presentation modes use a planar
 * close/open flip; grid cells, which never flip, draw only their current face.
 *
 * It is also the only card box in the client. Until 2026-09-07 seven surfaces
 * drew a bare `CardSvg` inside a box of their own — the grid, the turn dock,
 * the review chips and rows, the tutorial, the home fan, the card-back picker
 * — with four spellings of the corner, three of the shadow and one ratio that
 * was not 250:350. The scenery form became a `<span>` so it can sit inside a
 * button, and the two things those boxes actually varied became props:
 * `shadow`, and `cardBack` for the one picker that shows a back the player
 * has not chosen. Everything else a caller wants — a lean, a fan, a ghost's
 * opacity, a settle animation — goes on a wrapper around the card, not on it.
 */

/**
 * What a card casts. `flat` is the resting card everywhere; `raised` is a card
 * standing up (the count's lift, a scored card, a hand card pressed); `ink`
 * is the turn dock's hard 4px offset at every size, on lemon or white;
 * `none` is a thumbnail or a ghost preview.
 */
export type CardShadow = 'flat' | 'raised' | 'ink' | 'none';

const SHADOW: Record<CardShadow, string> = {
  flat: 'shadow-sm shadow-gray-400',
  raised: 'shadow-card-raised shadow-gray-400',
  ink: 'shadow-md shadow-ink',
  none: '',
};

export const CARD_SIZE = {
  inherited: '',
  hand: 'card-width-hand',
  table: 'card-width-table',
  seat: 'card-width-seat',
  'review-chip': 'card-width-review-chip',
  index: 'card-size-index',
  'back-loupe': 'card-size-back-loupe',
  'back-pair': 'card-size-back-pair',
  'picker-preview': 'card-size-picker-preview',
  cell: 'w-full',
  thumbnail: 'card-size-thumbnail',
  'dock-phone': 'card-size-dock-phone',
  'dock-laptop': 'card-size-dock-laptop',
  loupe: 'card-size-loupe',
  specimen: 'card-size-specimen',
  miniature: 'card-size-miniature',
  'seat-preview': 'card-size-seat-preview',
  'quiz-phone': 'card-size-quiz-phone',
  'quiz-laptop': 'card-size-quiz-laptop',
  'quiz-tray': 'card-size-quiz-tray',
  'quiz-hand': 'card-size-quiz-hand',
  'quiz-row': 'card-size-quiz-row',
  figure: 'card-size-figure',
  'figure-grid': 'card-size-figure-grid',
  'figure-row': 'card-size-figure-row',
  'figure-fit': 'card-size-figure-fit',
  'counter-slot': 'card-size-counter-slot',
  'counter-tray': 'card-size-counter-tray',
  'classic-phone': 'card-size-classic-phone',
  'classic-laptop': 'card-size-classic-laptop',
} as const;

/**
 * A ring round a card on the Classic felt: the seat's quiet stop, the mark a
 * pile's cards wear so whose they are still reads through a sweep's fade. On
 * the card itself rather than a wrapper, so the ring sits over the card's own
 * shadow the way one box-shadow list draws them.
 */
const RING = {
  none: '',
  you: 'ring-2 ring-teal-quiet',
  them: 'ring-2 ring-tangerine-quiet',
  periwinkle: 'ring-2 ring-periwinkle-quiet',
  /* The show's lit card: the seat's full stop, the ring the scoresheet's cell
     wears for the count being read. Lift and shadow alone did not read on the
     starter, which stands alone in its column (Mitch, 2026-09-18). */
  'you-lit': 'ring-2 ring-teal',
  'them-lit': 'ring-2 ring-tangerine',
  'periwinkle-lit': 'ring-2 ring-periwinkle',
} as const;

export type CardRing = keyof typeof RING;

export interface PlayingCardProps {
  /** Spatial cards own their HTML lift/rotation; flat cards let an SVG layout own it. */
  presentation?: 'spatial' | 'flat';
  /** Finite drawing sizes; tables inherit their measured card geometry by default. */
  size?: keyof typeof CARD_SIZE;
  card: Card | null;
  fourColour?: boolean;
  flightId?: string;
  button?: {
    selected: boolean;
    disabled: boolean;
    onSelect(card: Card): void;
    /**
     * What a card that does not answer looks like. `shade` darkens and drains
     * it, the table that shipped; `none` leaves the face alone, for a layout
     * that draws the withdrawal itself (the Classic hand drops a waiting card
     * to half strength on its own wrapper).
     */
    dim?: 'shade' | 'none';
  };
  /** The shadow the card casts at rest. Defaults to the flat card shadow. */
  shadow?: CardShadow;
  /** A seat's quiet ring round the card. None by default. */
  ring?: CardRing;
  /**
   * The back drawn when the card is face down. Defaults to the player's own
   * setting; the card-back picker passes each candidate so a player can see
   * a back before choosing it.
   */
  cardBack?: CardBack;
  /** Preview override; gameplay uses the saved front. */
  cardFront?: CardFront;
  /**
   * A dealt card drawn face down. Set at all, true or false, a grid cell
   * carries both faces like every other size, so the board can turn its Cut
   * Card over in place at the match start's door (`cross-heels`), at the
   * door's slower pace (`.card-turn` in `ui/css/cards.css`). Unset, a cell
   * draws only the face it shows, as it always has.
   */
  faceDown?: boolean;
}

/**
 * The card's shared box: shape, travel transform, corner, depth, and the flip
 * plumbing — everything true of a card whether it is scenery or a control.
 * The two renderings below carried this byte for byte; the button layers only
 * its control states (cursor, reset, hit slop, hover, focus, disabled,
 * selection) on top.
 */
const CARD = 'playing-card motion-off m-0 block flex-none touch-manipulation card-shape';

export function PlayingCard({
  card,
  fourColour = false,
  flightId,
  button,
  shadow = 'flat',
  ring = 'none',
  size = 'inherited',
  presentation = 'spatial',
  cardBack,
  cardFront,
  faceDown,
}: PlayingCardProps): React.JSX.Element {
  const cardName = useCardName();
  // The styles the table draws: a lapsed member's paid choice reads as free here.
  const chosenBack = useDrawnCardBack();
  const back = cardBack ?? chosenBack;
  const chosenFront = useDrawnCardFront();
  const front = cardFrontOrDefault(cardFront ?? chosenFront);
  const frontArt =
    card !== null && front !== 'shipped' ? (
      <CardFaceArt card={card} direction={front} fourColor={fourColour} />
    ) : (
      <CardSvg card={card} fourColour={fourColour} />
    );
  const spatial = presentation === 'spatial' && size !== 'cell';
  /* A cell carrying both faces turns at the match start door's pace. */
  const turning = size === 'cell' && faceDown !== undefined;
  const box = `${spatial ? 'card-spatial relative' : ''} ${CARD} ${SHADOW[shadow]} ${RING[ring]} ${CARD_SIZE[size]} ${turning ? 'card-turn' : ''}`;
  const faces =
    size === 'cell' && faceDown === undefined ? (
      card === null ? (
        <CardSvg card={null} cardBack={back} />
      ) : (
        frontArt
      )
    ) : (
      <svg
        className="pc-faces block size-full"
        viewBox="0 0 100 140"
        width="100%"
        height="100%"
        aria-hidden="true"
        focusable="false"
      >
        <g className="pc-front origin-center">{frontArt}</g>
        <g className="pc-back origin-center">
          <CardSvg card={null} cardBack={back} />
        </g>
      </svg>
    );
  const flight = flightId ?? `card-${card}`;
  const face = card === null || faceDown === true ? 'down' : 'up';

  // A face-down card has no name worth speaking, so it is never a control —
  // which keeps the button contract to exactly the cards a player can play.
  // A `<span>` rather than a `<div>`, because scenery has to be able to sit
  // inside a button: a grid cell and a review row are buttons with a card in
  // them, and a `<div>` may not.
  if (button === undefined || card === null || faceDown === true) {
    return (
      <span
        className={box}
        data-face={face}
        data-card-front={card === null ? undefined : front}
        data-presentation={presentation}
        data-flight-id={flight}
        aria-hidden="true"
      >
        {faces}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={`${box} cursor-pointer appearance-none border-0 bg-transparent p-0 ${spatial ? 'after:absolute' : ''} focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ink focus-visible:outline-solid disabled:cursor-default ${button.dim === 'none' ? '' : 'disabled:brightness-60 disabled:saturate-25'} aria-pressed:not-disabled:shadow-card-raised aria-pressed:not-disabled:shadow-gray-400`}
      data-face={face}
      data-card-front={card === null ? undefined : front}
      data-presentation={presentation}
      data-flight-id={flight}
      aria-pressed={button.selected}
      aria-label={cardName(card)}
      disabled={button.disabled}
      onClick={() => {
        button.onSelect(card);
      }}
    >
      {faces}
    </button>
  );
}

/**
 * The accessible name, unchanged from the DOM cards this replaces: the suite
 * navigates by "ace of spades". The words live in `en.json` under
 * `card.rank` / `card.suit`, shared with every other surface that speaks a
 * card, via `~/i18n/cardName.ts`.
 */
export function useCardName(): (card: Card) => string {
  const { t } = useTranslation();
  return (card) => cardName(t, card);
}
